import { useState } from 'react'
import { huskStiEtterInnlogging } from '../lib/etterInnlogging'
import { supabase } from '../lib/supabase'

// Siden brukeren sto på (for eksempel /skjermer/koble?kode=… fra QR-koden på en
// skjerm), så den åpnes igjen etter Microsoft-innloggingen og innloggingslenken.
// Begge kommer tilbake til forsiden (src/lib/etterInnlogging.js).
const huskSiden = () => huskStiEtterInnlogging(`${window.location.pathname}${window.location.search}`)

// Tre modi:
//   'password' — e-post + passord (standard)
//   'magic'    — send engangs-innloggingslenke på e-post
//   'forgot'   — send tilbakestill-passord-lenke
export default function Login({ unauthorized }) {
  const [mode, setMode] = useState('password')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [sent, setSent] = useState(false)
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)

  const resetFlash = () => { setError(null); setSent(false) }

  const handlePasswordLogin = async (e) => {
    e.preventDefault()
    resetFlash()
    setLoading(true)
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) setError(translateAuthError(error.message))
    setLoading(false)
    // Ved suksess tar AuthContext over via onAuthStateChange
  }

  const handleMagicLink = async (e) => {
    e.preventDefault()
    resetFlash()
    setLoading(true)
    huskSiden()
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: window.location.origin },
    })
    if (error) setError(translateAuthError(error.message))
    else setSent(true)
    setLoading(false)
  }

  const handleForgot = async (e) => {
    e.preventDefault()
    resetFlash()
    setLoading(true)
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    })
    if (error) setError(translateAuthError(error.message))
    else setSent(true)
    setLoading(false)
  }

  // Microsoft SSO via Azure AD. Supabase-provider-navnet er 'azure'.
  // Krever at provideren er aktivert i Supabase-prosjektet (Auth → Providers
  // → Azure) med Application (client) ID + secret fra Entra ID-app-
  // registreringen. Tenant-id settes på Supabase-siden ("Azure Tenant URL"
  // = https://login.microsoftonline.com/<tenant-id>/v2.0).
  // Backend (`requireAuth`) krever fortsatt at e-posten finnes i `app_users`,
  // så invite-only-flyten beholdes uendret.
  const handleMicrosoftLogin = async () => {
    resetFlash()
    setLoading(true)
    huskSiden()
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'azure',
      options: {
        scopes: 'email openid profile',
        redirectTo: window.location.origin,
      },
    })
    if (error) {
      setError(translateAuthError(error.message))
      setLoading(false)
    }
    // Ved suksess redirect-er nettleseren til Microsoft og kommer tilbake
    // med token i URL-fragmentet — AuthContext.onAuthStateChange tar over.
  }

  return (
    <div className="min-h-screen bg-bg flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-text">Pulse</h1>
          <p className="text-text-muted text-sm mt-1">
            {mode === 'forgot' ? 'Tilbakestill passord' : 'Logg inn for å fortsette'}
          </p>
        </div>

        <div className="bg-card rounded-xl border border-border p-6">
          {unauthorized && (
            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
              Du har ikke tilgang. Kontakt administrator.
            </div>
          )}

          {sent ? (
            <div className="text-center py-4">
              <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-green-500/10 flex items-center justify-center">
                <svg className="w-6 h-6 text-green-400" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" />
                </svg>
              </div>
              <h2 className="text-lg font-medium text-text mb-1">Sjekk e-posten din</h2>
              <p className="text-text-muted text-sm">
                {mode === 'forgot'
                  ? <>Vi har sendt en tilbakestillings-lenke til <strong className="text-text">{email}</strong></>
                  : <>Vi har sendt en innloggingslenke til <strong className="text-text">{email}</strong></>}
              </p>
              <button
                onClick={() => { setSent(false); setMode('password') }}
                className="mt-4 text-sm text-primary hover:text-primary-light transition-colors"
              >
                Tilbake til innlogging
              </button>
            </div>
          ) : mode === 'forgot' ? (
            <form onSubmit={handleForgot}>
              <label className="block text-sm font-medium text-text-muted mb-1.5">E-post</label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="din@epost.no"
                required
                autoFocus
                className="w-full bg-surface border border-border rounded-lg px-3 py-2.5 text-text placeholder:text-text-muted/50 focus:outline-none focus:border-primary transition-colors"
              />
              {error && <p className="mt-2 text-sm text-red-400">{error}</p>}
              <button
                type="submit"
                disabled={loading || !email}
                className="w-full mt-4 py-2.5 bg-primary text-white rounded-lg font-medium hover:bg-primary-dark transition-colors disabled:opacity-50"
              >
                {loading ? 'Sender...' : 'Send tilbakestillings-lenke'}
              </button>
              <button
                type="button"
                onClick={() => { resetFlash(); setMode('password') }}
                className="w-full mt-3 text-sm text-text-muted hover:text-text transition-colors"
              >
                Tilbake
              </button>
            </form>
          ) : mode === 'magic' ? (
            <form onSubmit={handleMagicLink}>
              <label className="block text-sm font-medium text-text-muted mb-1.5">E-post</label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="din@epost.no"
                required
                autoFocus
                className="w-full bg-surface border border-border rounded-lg px-3 py-2.5 text-text placeholder:text-text-muted/50 focus:outline-none focus:border-primary transition-colors"
              />
              {error && <p className="mt-2 text-sm text-red-400">{error}</p>}
              <button
                type="submit"
                disabled={loading || !email}
                className="w-full mt-4 py-2.5 bg-primary text-white rounded-lg font-medium hover:bg-primary-dark transition-colors disabled:opacity-50"
              >
                {loading ? 'Sender...' : 'Send innloggingslenke'}
              </button>
              <div className="mt-4 pt-4 border-t border-border space-y-3">
                <MicrosoftButton onClick={handleMicrosoftLogin} disabled={loading} />
                <button
                  type="button"
                  onClick={() => { resetFlash(); setMode('password') }}
                  className="block w-full text-center text-sm text-text-muted hover:text-text transition-colors"
                >
                  Logg inn med passord i stedet
                </button>
              </div>
            </form>
          ) : (
            <form onSubmit={handlePasswordLogin}>
              <label className="block text-sm font-medium text-text-muted mb-1.5">E-post</label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="din@epost.no"
                required
                autoFocus
                autoComplete="email"
                className="w-full bg-surface border border-border rounded-lg px-3 py-2.5 text-text placeholder:text-text-muted/50 focus:outline-none focus:border-primary transition-colors"
              />

              <div className="flex items-baseline justify-between mt-4 mb-1.5">
                <label className="block text-sm font-medium text-text-muted">Passord</label>
                <button
                  type="button"
                  onClick={() => { resetFlash(); setMode('forgot') }}
                  className="text-xs text-primary hover:text-primary-light transition-colors"
                >
                  Glemt passord?
                </button>
              </div>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                autoComplete="current-password"
                className="w-full bg-surface border border-border rounded-lg px-3 py-2.5 text-text placeholder:text-text-muted/50 focus:outline-none focus:border-primary transition-colors"
              />

              {error && <p className="mt-2 text-sm text-red-400">{error}</p>}

              <button
                type="submit"
                disabled={loading || !email || !password}
                className="w-full mt-4 py-2.5 bg-primary text-white rounded-lg font-medium hover:bg-primary-dark transition-colors disabled:opacity-50"
              >
                {loading ? 'Logger inn...' : 'Logg inn'}
              </button>

              <div className="mt-4 pt-4 border-t border-border space-y-3">
                <MicrosoftButton onClick={handleMicrosoftLogin} disabled={loading} />
                <button
                  type="button"
                  onClick={() => { resetFlash(); setMode('magic') }}
                  className="block w-full text-center text-sm text-text-muted hover:text-text transition-colors"
                >
                  Eller: send meg en innloggingslenke på e-post
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}

// Microsoft-merkevare-knapp. Følger Microsofts retningslinjer:
// hvit bakgrunn, svart "Sign in with Microsoft"-tekst, fire-firkants-logo.
function MicrosoftButton({ onClick, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="w-full flex items-center justify-center gap-2.5 py-2.5 bg-white text-gray-800 border border-gray-300 rounded-lg font-medium hover:bg-gray-50 transition-colors disabled:opacity-50"
    >
      <svg width="18" height="18" viewBox="0 0 23 23" aria-hidden="true">
        <rect x="1" y="1" width="10" height="10" fill="#F25022" />
        <rect x="12" y="1" width="10" height="10" fill="#7FBA00" />
        <rect x="1" y="12" width="10" height="10" fill="#00A4EF" />
        <rect x="12" y="12" width="10" height="10" fill="#FFB900" />
      </svg>
      <span>Logg inn med Microsoft</span>
    </button>
  )
}

// Supabase-feilmeldinger er på engelsk — oversett de vanligste
function translateAuthError(msg) {
  if (!msg) return 'Innlogging feilet'
  const m = msg.toLowerCase()
  if (m.includes('invalid login credentials')) return 'Feil e-post eller passord'
  if (m.includes('email not confirmed')) return 'E-posten er ikke bekreftet. Sjekk innboksen eller bruk "Glemt passord?" for å sette et.'
  if (m.includes('user not found')) return 'Finner ingen bruker med den e-posten'
  if (m.includes('rate limit')) return 'For mange forsøk — vent litt og prøv igjen'
  if (m.includes('password should be at least')) return 'Passord må være minst 6 tegn'
  return msg
}
